/* eslint-disable no-console */
import { useEffect, useRef, useState } from 'react';
import styles from './HrDashboard.module.scss';
import Layout from 'src/layout/Layout';
import AgentTotalCallsAndTime from './AgentTotalCallsAndTime';
import {
  EXTENSION_LISTENER_TYPES,
  ExtensionHandler,
  ExtensionHelper
} from 'src/utils/extension.utils';
import { useDispatch, useSelector } from 'react-redux';
import store, { RootState } from 'src/store';
import {
  refreshCallData,
  setCallId,
  setCampaignId,
  setIsAgentOnline,
  setIsAmeyoErroneous,
  setIsCallConnected,
  setIsFeedbackBtnEnable,
  setIsFeedbackPending,
  setIsSidebarSwitchDisable,
  setManualLoginCreds,
  setShowMainScreen,
  setSlackNotificationSent
} from 'src/reducers/humanReminderSlice';
import {
  AMEYO_CALL_EVENTS,
  AMEYO_STATUS_CODES,
  AMEYO_STATUS_RANKS,
  ErrorTypes,
  IAmeyoRecalibratedData
} from 'src/service/naviExtension.service';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import {
  getAgentStats,
  getCustomerDetails,
  getCustomerDetailsRecalibrate,
  setAgentAvailability,
  setCallStatus,
  setCallStatusDisconnected
} from './dc-97/HumanReminderAction';
import { AudioPlayer } from '@cp/components/AudioPlayer/AudioPlayer';
import ameyoRinging from '../../assets/audio/ring.wav';
import CallWaitingScreen from './dc-97/CallWaitingScreen';
import {
  resetHumanReminderCustomerDetails,
  setHumanReminderCustomerDetailsLoading
} from 'src/reducers/commonSlice';
import HrCustomerDetails from './HrCustomerDetails';
import { HRC_CLICKSTREAM, HRC_EVENTS } from '../../service/clickStream.constant';
import { addClickstreamEvent } from '../../service/clickStreamEventService';
import { HRC_CALL_DISCONNECT_SOURCE_TYPE } from 'src/constants/Common.constants';
import {
  AGENT_AVAILABILITY,
  ameyoErroneousErrorMessage,
  BACKEND_AUDIT_STATUSES,
  HRC_ALERTS_SLACK_CHANNEL,
  HRC_API_TIMEOUT,
  PreviousReminderRefId,
  SLACK_NOTIFICATION_INTERVAL,
  spadeCallingCampaignId
} from './dc-97/dc97Constant';
import { pushToLocalStorage } from '@cp/utils/StorageUtils';
import { noop } from '@utils/common';
import { resetState } from '@cp/reducers/ameyoSlice';
import isLitmusExperimentEnabled from '@cp/utils/isLitmusExperimentEnabled';
import { LITMUS_EXPERIMENT_NAMES } from '@cp/constants/litmusExperimentNames';
import {
  AMEYO_DIAGNOSTIC_EVENT_INTERVAL,
  AmeyoDiagnosticEventGroup,
  AmeyoDiagnosticEventNames,
  AmeyoDiagnosticsMetricTypes,
  GAUGE_METRIC
} from '@cp/constants/ameyoConstants';
import useAmeyoDiagnostics from '@cp/hooks/useAmeyoDiagnostics';
import { poll } from '@cp/utils/polling';
import { isFunction } from '@cp/utils/commonUtils';

const HrDashBoard = () => {
  const dispatch = useDispatch();
  const [showCallingPopup, setShowCallingPopup] = useState(false);
  const [isAuthBlocked, setIsAuthBlocked] = useState(false);
  const [isMultipleCampaigns, setIsMultipleCampaigns] = useState(false);
  const slackNotificationToTL = useRef<NodeJS.Timeout>();
  let authError = false;
  const showMainScreen = useSelector((state: RootState) => state.humanReminder.showMainScreen);
  const userData = useSelector((state: RootState) => state.common.userData);
  const humanReminderAgentDetails = useSelector(
    (state: RootState) => state.common.humanReminderAgentDetails
  );
  const reminderRefId = useSelector((state: RootState) => state.humanReminder.reminderRefId);
  const campaignId = useSelector((state: RootState) => state.humanReminder.campaignId);
  const customerDetails = useSelector(
    (state: RootState) =>
      state?.common?.humanReminderCustomerDetails?.humanReminderResponse?.customerDetails
  );
  const isAgentOnline = useSelector((state: RootState) => state.humanReminder.isAgentOnline);
  const isSidebarSwitchDisable = useSelector(
    (state: RootState) => state?.humanReminder?.isSidebarSwitchDisable
  );
  const isAmeyoErroneous = useSelector((state: RootState) => state.humanReminder.isAmeyoErronous);
  const ameyoUserId = useSelector((state: RootState) => state.humanReminder.ameyoManualLoginEmail);
  const ameyoPassword = useSelector(
    (state: RootState) => state.humanReminder.ameyoManualLoginEmail
  );
  const userReferenceId = useSelector((state: RootState) => state?.common?.userData?.referenceId);
  const disconnectApiLoading = useSelector(
    (state: RootState) => state?.humanReminder?.disconnectApiLoading
  );
  const callId = useSelector((state: RootState) => state?.humanReminder?.callId);

  const callDisconnectSource = useRef<HRC_CALL_DISCONNECT_SOURCE_TYPE | null>(null);
  const { sendAmeyoDiagnostics } = useAmeyoDiagnostics();

  const sendSlackNotification = (callId: string) => {
    setAgentAvailability(AGENT_AVAILABILITY.AGENT_IDLE, callId);
    clearInterval(slackNotificationToTL.current);
    dispatch(setSlackNotificationSent(true));
  };

  const getReminderRefId = () => {
    return store.getState()?.humanReminder.reminderRefId;
  };
  const getSlackNotificationStatus = () => {
    return store.getState()?.humanReminder?.slackNotificationSent;
  };

  function resetCallData() {
    ExtensionHandler.disposeCall();
    ExtensionHandler.resetCallStatus();
    ExtensionHandler.updateIsPending(false);
    dispatch(resetHumanReminderCustomerDetails(null));
    dispatch(refreshCallData(null));
    localStorage.removeItem('persist:root');
    clearTimeout(slackNotificationToTL.current);
    dispatch(getAgentStats());
    callDisconnectSource.current = null;
  }

  //Api Calls for Agent and Customer Details
  useEffect(() => {
    dispatch(getAgentStats());
    const refId = getReminderRefId();
    const slackNotificationStatus = getSlackNotificationStatus();
    if (callId) {
      ExtensionHandler.getCurrentCallStatus();

      dispatch(setHumanReminderCustomerDetailsLoading(true));
      dispatch(
        getCustomerDetails(callId, () => {
          ExtensionHandler.disconnectCall();
          resetCallData();
          dispatch(setShowMainScreen(false));
          setShowCallingPopup(false);
        })
      );
    }
    if (ExtensionHandler.currentCallStatus().disconnectedAt && callId && !slackNotificationStatus) {
      const timeDiffAfterDisconnect =
        Date.now() - ExtensionHandler.currentCallStatus().disconnectedAt;
      clearTimeout(slackNotificationToTL.current);
      if (timeDiffAfterDisconnect < SLACK_NOTIFICATION_INTERVAL) {
        slackNotificationToTL.current = setTimeout(() => {
          sendSlackNotification(callId);
        }, SLACK_NOTIFICATION_INTERVAL - timeDiffAfterDisconnect);
      } else {
        sendSlackNotification(callId);
      }
    }
  }, []);

  //initialState Setup
  useEffect(() => {
    const isCallRinging = ExtensionHandler.currentCallStatus().acceptedAt
      ? false
      : ExtensionHandler.currentCallStatus().ringingAt
      ? true
      : false;
    dispatch(setIsAgentOnline(ExtensionHandler.isAvailable()));
    setShowMainScreen(!!ExtensionHandler.currentCallStatus().acceptedAt);
    setShowCallingPopup(isCallRinging);
  }, []);

  //login
  useEffect(() => {
    if (
      userData?.email &&
      userData?.ameyoKey &&
      ExtensionHandler.currentAmeyoStatus() === AMEYO_STATUS_CODES.LOGGED_OUT
    ) {
      //sending hardcode credntials for now
      ExtensionHandler.login(userData.email, userData.ameyoKey);
      //ExtensionHandler.login('test@navi.com', 'Exotel@123$');
    }
  }, [userData]);

  const extensionOnPolling = useRef<() => void>();
  useEffect(() => {
    if (isAgentOnline) {
      extensionOnPolling.current = poll(
        () => {
          sendAmeyoDiagnostics({
            metricName: AmeyoDiagnosticEventNames.extensionOnTime.name,
            metricType: AmeyoDiagnosticEventNames.extensionOnTime.metricType,
            value: '',
            group: AmeyoDiagnosticEventGroup.HRC
          });
        },
        undefined,
        AMEYO_DIAGNOSTIC_EVENT_INTERVAL,
        noop
      );
    } else {
      isFunction(extensionOnPolling.current) && extensionOnPolling.current();
    }
    return () => {
      isFunction(extensionOnPolling.current) && extensionOnPolling.current();
    };
  }, [isAgentOnline]);

  const onCallAcceptedHandler = () => {
    ExtensionHandler.acceptCall();
    setShowCallingPopup(false);
    callDisconnectSource.current = HRC_CALL_DISCONNECT_SOURCE_TYPE.USER;
  };

  const onCallRejectedHandler = () => {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_REJECT_BUTTON_CLICKED], {
      currentCallStatus: ExtensionHandler.currentCallStatus(),
      campaignId,
      callId,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus()
    });
    ExtensionHandler.rejectCall(() => {
      addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_REJECT_SUCCESSFULL], {
        currentCallStatus: ExtensionHandler.currentCallStatus(),
        campaignId,
        callId,
        ameyoStatus: ExtensionHandler.currentAmeyoStatus()
      });
    });
    //Suggested by Ishan ExtensionHandler.resetCallStatus();
    setShowCallingPopup(false);
  };
  function focusAmeyoTab() {
    ExtensionHandler.focusAmeyoTab();
    addClickstreamEvent(HRC_CLICKSTREAM.HRC_AMEYO_WINDOW_OPEN, {
      email: ameyoUserId ?? userData?.email,
      password: ameyoPassword ?? userData?.ameyoKey,
      callId,
      campaignId,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus()
    });
  }

  const handleSlackNotificationToTL = () => {
    if (!callId) {
      return;
    }
    clearTimeout(slackNotificationToTL.current);
    slackNotificationToTL.current = setTimeout(() => {
      sendSlackNotification(callId);
    }, SLACK_NOTIFICATION_INTERVAL);
  };

  const disConnectHandler = () => {
    callDisconnectSource.current = HRC_CALL_DISCONNECT_SOURCE_TYPE.AGENT;
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.DISCONNECT_BUTTON_CLICKED], {
      currentCallStatus: ExtensionHandler.currentCallStatus(),
      campaignId,
      callId: callId
    });
    ExtensionHandler.disconnectCall();
    ExtensionHandler.disposeCall();
  };

  const onMultipleCampaignsNextButtonClicked = () => {
    setIsMultipleCampaigns(false);
  };
  const ameyoGenericClickstreamCallback = (messageData: any) => {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_GENERIC_CLICKSTREAM], {
      campaignId,
      data: messageData,
      reminderRefId: localStorage.getItem(PreviousReminderRefId) || ''
    });
  };

  const onAmeyoErroneous = () => {
    toast(ameyoErroneousErrorMessage, { type: 'error' });
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_AMEYO_ERRONEOUS], {
      campaignId,
      agentReferenceId: userData?.referenceId
    });
    sendAmeyoDiagnostics({
      metricName: AmeyoDiagnosticEventNames.ameyoErroneousCount.name,
      metricType: AmeyoDiagnosticEventNames.ameyoErroneousCount.metricType,
      value: '',
      group: AmeyoDiagnosticEventGroup.HRC
    });
    dispatch(setIsAmeyoErroneous(true));
  };

  const ameyoBackgroundTabOpenedCallback = () => {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_AMEYO_WINDOW_BACKGROUND_OPEN_SUCCESS], {
      email: ameyoUserId ?? userData?.email,
      callId: callId,
      campaignId,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus()
    });
  };

  const ameyoTabOpeningAttemptCallback = (data: any) => {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_AMEYO_WINDOW_BACKGROUND_OPEN_ATTEMPT], {
      ...data
    });
  };

  const ameyoBackgroundTabOpenFailedCallback = (data: any) => {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_AMEYO_WINDOW_BACKGROUND_OPEN_FAILURE], {
      ...data
    });
  };

  const onToggleAmeyoStatus = (onToggleAmeyoStatusData: {
    ameyoTogglePrevStatus: 'OFF' | 'ON';
    ameyoToggleNewStatus: 'OFF' | 'ON';
  }) => {
    sendAmeyoDiagnostics({
      metricName: AmeyoDiagnosticEventNames.extensionStatus.name,
      metricType: AmeyoDiagnosticEventNames.extensionStatus.metricType,
      value:
        onToggleAmeyoStatusData.ameyoToggleNewStatus === 'ON' ? GAUGE_METRIC.ON : GAUGE_METRIC.OFF,
      group: AmeyoDiagnosticEventGroup.HRC
    });
    addClickstreamEvent(HRC_CLICKSTREAM.HRC_TOGGLE_AMEYO_CLICKED, {
      reminderRefId,
      campaignId,
      aggentReferenceId: userData?.referenceId,
      previousToggleStatus: onToggleAmeyoStatusData.ameyoTogglePrevStatus,
      newToggleStatus: onToggleAmeyoStatusData.ameyoToggleNewStatus,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus()
    });

    if (onToggleAmeyoStatusData.ameyoToggleNewStatus === 'OFF') {
      if (isAgentOnline) {
        ExtensionHandler.setAvailability(!isAgentOnline);
        dispatch(setIsSidebarSwitchDisable(true));
      }
    } else {
      dispatch(setIsSidebarSwitchDisable(false));
    }
  };

  const extensionToggleMidCallCallback = () => {
    addClickstreamEvent(HRC_CLICKSTREAM.HRC_AMEYO_TOGGLE_MID_CALL_CLICK, {
      reminderRefId: getReminderRefId(),
      campaignId,
      aggentReferenceId: userData?.referenceId
    });
  };

  const onAmeyoReloadedCallback = () => {
    addClickstreamEvent(HRC_CLICKSTREAM.HRC_AMEYO_RELOADED, {
      campaignId,
      aggentReferenceId: userData?.referenceId
    });
  };

  const onAmeyoDiagnosticEventCallback = (messageType: string) => {
    sendAmeyoDiagnostics({
      metricName: messageType,
      metricType: AmeyoDiagnosticEventNames?.[messageType]
        ?.metricType as AmeyoDiagnosticsMetricTypes,
      value: '',
      group: AmeyoDiagnosticEventGroup.HRC
    });
  };

  useEffect(() => {
    const onCallInComingCallback = (
      phoneNumber: number,
      lan: string,
      crtObjectId: string,
      callId: string
    ) => {
      addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_CALL_RINGING_EVENT], {
        showCallingPopup,
        currentCallStatus: ExtensionHandler.currentCallStatus(),
        lan,
        phoneNumber,
        reminderRefId,
        campaignId,
        ameyoStatus: ExtensionHandler.currentAmeyoStatus()
      });
      if (!ExtensionHandler.getIsPending()) {
        setShowCallingPopup(true);
        dispatch(setCallId(callId));
        addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.POPUP_SHOWN], {
          showCallingPopup,
          currentCallStatus: ExtensionHandler.currentCallStatus(),
          lan,
          phoneNumber,
          reminderRefId,
          campaignId,
          ameyoStatus: ExtensionHandler.currentAmeyoStatus()
        });
      } else {
        ExtensionHandler.rejectCall();
        addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_MISSED_CALL_SINCE_AGENT_IS_ON_CALL], {
          currentCallStatus: ExtensionHandler.currentCallStatus(),
          lan,
          phoneNumber,
          reminderRefId,
          campaignId,
          ameyoStatus: ExtensionHandler.currentAmeyoStatus()
        });
        toast('you have missed a call', { type: 'error' });
      }
    };
    const onCallConnectedCallback = (phoneNumber: number, lan: string, crtObjectId: string) => {
      addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.ACCEPT_EVENT_RECEIVED], {
        phoneNumber,
        lan,
        crtObjectId,
        currentCallStatus: ExtensionHandler.currentCallStatus(),
        reminderRefId,
        campaignId,
        ameyoStatus: ExtensionHandler.currentAmeyoStatus()
      });
      dispatch(setHumanReminderCustomerDetailsLoading(true));
      dispatch(
        setCallStatus(
          String(phoneNumber),
          lan,
          crtObjectId,
          ExtensionHandler.currentCallStatus().acceptedAt,
          (_error: any) => {
            ExtensionHandler.disconnectCall();
            resetCallData();
            dispatch(setShowMainScreen(false));
            setShowCallingPopup(false);
            toast(`Error! Please contact support. We are connecting you to the next call.`, {
              type: 'error',
              autoClose: 6500
            });
          },
          campaignId,
          reminderRefId
        )
      );
      dispatch(setShowMainScreen(true));
      dispatch(setIsFeedbackPending(true));
      dispatch(setIsCallConnected(true));
      dispatch(setIsFeedbackBtnEnable(false));
      setShowCallingPopup(false);
      ExtensionHandler.updateIsPending(true);
    };
    const onAvailabilityChangeCallback = (isAvailable: boolean) => {
      dispatch(setIsAgentOnline(isAvailable));
      dispatch(setIsFeedbackBtnEnable(true));
      setAgentAvailability(
        isAvailable ? AGENT_AVAILABILITY.AGENT_ONLINE : AGENT_AVAILABILITY.AGENT_OFFLINE
      );
      sendAmeyoDiagnostics({
        metricName: AmeyoDiagnosticEventNames.ameyoAvailabilityStatus.name,
        metricType: AmeyoDiagnosticEventNames.ameyoAvailabilityStatus.metricType,
        value: isAvailable ? GAUGE_METRIC.ON : GAUGE_METRIC.OFF,
        group: AmeyoDiagnosticEventGroup.HRC
      });
    };

    const onSendCurrentCallStatus = async (ameyoData: IAmeyoRecalibratedData) => {
      const isEnabled = await isLitmusExperimentEnabled(
        LITMUS_EXPERIMENT_NAMES.HRC_RECALIBRATE_ENABLE,
        {
          'x-customer-id': userReferenceId
        }
      );
      if (!isEnabled.result) {
        return;
      }
      const customerDetails = await getCustomerDetailsRecalibrate(callId || '');
      const currentBackendStatus = customerDetails?.data?.body?.status;
      if (
        (ameyoData?.callStatus === AMEYO_CALL_EVENTS.DISCONNECTED ||
          ameyoData?.callStatus === AMEYO_CALL_EVENTS.IDLE) &&
        currentBackendStatus === BACKEND_AUDIT_STATUSES.ACTIVE
      ) {
        //call disconnect api again
        dispatch(
          setCallStatusDisconnected({
            humanReminderReferenceId: reminderRefId || '',
            agentRefId: userData?.referenceId || '',
            campaignId,
            cleanup: () => {
              dispatch(setIsFeedbackBtnEnable(true));
              dispatch(setIsCallConnected(false));
              ExtensionHandler.updateIsPending(true);
              handleSlackNotificationToTL();
            },
            phoneNumber: customerDetails?.data?.body?.customerDetails?.connectedNumber + '',
            lan: customerDetails?.data?.body?.loanDetails?.loanAccountNumber,
            crtObjectId: ''
          })
        );
      } else if (
        (ameyoData?.callStatus === AMEYO_CALL_EVENTS.DISCONNECTED ||
          ameyoData?.callStatus === AMEYO_CALL_EVENTS.IDLE) &&
        currentBackendStatus === BACKEND_AUDIT_STATUSES.DISPOSED
      ) {
        //resetting state and go back to main screen
        dispatch(resetState());
      } else if (
        (ameyoData?.callStatus === AMEYO_CALL_EVENTS.DISCONNECTED ||
          ameyoData?.callStatus === AMEYO_CALL_EVENTS.IDLE) &&
        currentBackendStatus === BACKEND_AUDIT_STATUSES.DISCONNECTED
      ) {
        //set feedback button enabled again
        dispatch(setIsFeedbackBtnEnable(true));
      }
    };

    const onErrorCallback = (err: any) => {
      // TODO : if >2 branching is there, make the following as switch case and not if else
      if (err?.type === ErrorTypes.AUTH_ERROR) {
        if (userData?.email && userData?.ameyoKey && userData?.token) setIsAuthBlocked(true);
      } else if (err?.type === ErrorTypes.MULTIPLE_CAMPAIGNS) {
        setIsMultipleCampaigns(true);
      } else if (
        err?.type !== ErrorTypes.HIDDEN &&
        err?.type !== ErrorTypes.DOM_ELEMENT_NOT_FOUND
      ) {
        console.log(err);
        toast(err?.errorMessage, { type: 'error' });
      }
    };
    const onAmeyoStateChangeCallback = (status: AMEYO_STATUS_CODES) => {
      // if change is there and the status is loggedIn or greater, hide the "manual intervention dialogue"
      if (
        AMEYO_STATUS_RANKS[status]?.occuranceOrder >=
        AMEYO_STATUS_RANKS[AMEYO_STATUS_CODES.LOGGED_IN]?.occuranceOrder
      ) {
        setIsAuthBlocked(false);
      }
      if (
        userData?.email &&
        userData?.ameyoKey &&
        status === AMEYO_STATUS_CODES.LOGGED_OUT &&
        userData?.token
      ) {
        ExtensionHandler.login(userData.email, userData.ameyoKey);
      } else if (status === AMEYO_STATUS_CODES.READY) {
        toast('Ameyo is Ready', { type: 'success', autoClose: 5000 });

        if (isSidebarSwitchDisable) {
          dispatch(setIsSidebarSwitchDisable(true));
        }

        if (isAmeyoErroneous) {
          ExtensionHandler.setAvailability(true);
          dispatch(setIsAmeyoErroneous(false));
        }
      } else if (status === AMEYO_STATUS_CODES.LOGGED_IN) {
        if (authError) {
          addClickstreamEvent(HRC_CLICKSTREAM.HRC_AMEYO_LOGIN_MANUAL_SUCCESSFUL, {
            email: ameyoUserId ?? userData?.email,
            reminderRefId,
            campaignId,
            ameyoStatus: ExtensionHandler.currentAmeyoStatus()
          });
        } else {
          addClickstreamEvent(HRC_CLICKSTREAM.HRC_AMEYO_LOGIN_AUTO_SUCCESSFUL, {
            email: ameyoUserId ?? userData?.email,
            reminderRefId,
            campaignId,
            ameyoStatus: ExtensionHandler.currentAmeyoStatus()
          });
        }
        authError = false;
      } else if (status === AMEYO_STATUS_CODES.AUTH_ERROR) {
        addClickstreamEvent(HRC_CLICKSTREAM.HRC_AMEYO_LOGIN_FAILURE, {
          email: userData?.email,
          ameyoKey: userData?.ameyoKey,
          reminderRefId,
          campaignId,
          ameyoStatus: ExtensionHandler.currentAmeyoStatus()
        });
        authError = true;
      }
    };

    const manualLoginCallback = (data: any) => {
      dispatch(setManualLoginCreds(data));
    };

    const onCampaignIdRecievedCallback = (campaignId: string) => {
      dispatch(setCampaignId(campaignId));
    };
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.onCallInComing,
      onCallInComingCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.onCallConnected,
      onCallConnectedCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.onAvailabilityChange,
      onAvailabilityChangeCallback
    );
    ExtensionHelper.addExtensionListener(EXTENSION_LISTENER_TYPES.onError, onErrorCallback);
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.onAmeyoStateChange,
      onAmeyoStateChangeCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.campaignId,
      onCampaignIdRecievedCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.multipleCampaignsNextButtonClicked,
      onMultipleCampaignsNextButtonClicked
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.toggleAmeyoStatus,
      onToggleAmeyoStatus
    );
    ExtensionHelper.addExtensionListener(EXTENSION_LISTENER_TYPES.ameyoErroneous, onAmeyoErroneous);
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.ameyoGenericClickstream,
      ameyoGenericClickstreamCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.ameyoBackgroundTabOpened,
      ameyoBackgroundTabOpenedCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.ameyoTabOpeningAttempt,
      ameyoTabOpeningAttemptCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.ameyoBackgroundTabOpenFailed,
      ameyoBackgroundTabOpenFailedCallback
    );
    ExtensionHelper.addExtensionListener(EXTENSION_LISTENER_TYPES.manualLogin, manualLoginCallback);
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.extensionToggleMidCallClickEvent,
      extensionToggleMidCallCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.ameyoReloaded,
      onAmeyoReloadedCallback
    );

    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.sendCurrentCallStatusAndRecalibrate,
      onSendCurrentCallStatus
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.acceptCallClickFailure,
      onAmeyoDiagnosticEventCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.rejectCallClickFailure,
      onAmeyoDiagnosticEventCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.disconnectCallClickFailure,
      onAmeyoDiagnosticEventCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.disposeCallClickFailure,
      onAmeyoDiagnosticEventCallback
    );
  }, []);

  const onCallDisConnectedCallback = (phoneNumber: number, lan: string, crtObjectId: string) => {
    const reminderRefId = getReminderRefId();
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.DISCONNECT_EVENT_RECEIVED], {
      phoneNumber,
      lan,
      crtObjectId,
      currentCallStatus: ExtensionHandler.currentCallStatus(),
      campaignId,
      humanReminderReferenceId: reminderRefId,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus(),
      customerRefId: customerDetails?.customerReferenceId,
      agentRefrenceId: userData?.referenceId,
      source: callDisconnectSource.current
    });

    if (ExtensionHandler.currentCallStatus().acceptedAt) {
      setTimeout(() => {
        if (disconnectApiLoading) {
          ExtensionHandler.getCurrentCallStatus();
        } else {
          return;
        }
      }, HRC_API_TIMEOUT);
      dispatch(
        setCallStatusDisconnected({
          humanReminderReferenceId: reminderRefId || '',
          agentRefId: userData?.referenceId || '',
          campaignId,
          cleanup: () => {
            dispatch(setIsFeedbackBtnEnable(true));
            dispatch(setIsCallConnected(false));
            ExtensionHandler.updateIsPending(true);
            handleSlackNotificationToTL();
          },
          phoneNumber: phoneNumber + '',
          lan,
          crtObjectId
        })
      );
    } else {
      setShowCallingPopup(false);
    }
  };

  ExtensionHelper.addExtensionListener(
    EXTENSION_LISTENER_TYPES.onCallDisConnected,
    onCallDisConnectedCallback
  );

  useEffect(() => {
    reminderRefId && pushToLocalStorage(PreviousReminderRefId, reminderRefId);
  }, [reminderRefId]);

  return (
    <Layout>
      <div className={styles.agentTodaysData}>
        <AgentTotalCallsAndTime
          totalCallsMade={humanReminderAgentDetails?.totalCalls}
          onlineTime={humanReminderAgentDetails?.onlineTime}
          requestProcessedAt={humanReminderAgentDetails?.requestProcessedAt}
        />
      </div>
      {showMainScreen ? (
        <HrCustomerDetails resetCallData={resetCallData} disConnectHandler={disConnectHandler} />
      ) : (
        <CallWaitingScreen
          showCallIncomingPopup={showCallingPopup}
          showAuthBlockedPopup={isAuthBlocked}
          showMultipleCampaignsPopup={isMultipleCampaigns}
          focusAmeyoHandler={focusAmeyoTab}
          onCallAcceptHandler={onCallAcceptedHandler}
          onCallRejectHandler={onCallRejectedHandler}
        />
      )}

      {showCallingPopup ? <AudioPlayer fileName={ameyoRinging}></AudioPlayer> : <></>}
    </Layout>
  );
};

export default HrDashBoard;
